import { useTranslation } from "next-i18next";
import strftime from "strftime";
import { Get_SubscriptionQuery, SubscriptionStatus, BillingFrequency } from "gql/graphql";

import ButtonOrLink from "components/Button/ButtonOrLink";
import {
  SubscriptionDetailRow,
  SubscriptionDetailTitleRow,
  SubscriptionDetailSubtitleRow,
  CopyIcon,
} from "components/SubscriptionDetailRow/SubscriptionDetailRow";
import CopyToClipboardButton from "components/Button/CopyToClipboardButton";
import CancellationMessage from "./Components/CancellationMessage";
import CustomerSupportMessage from "./Components/CustomerSupportMessage";

export default function SubscriptionDetailTable({
  subscription,
}: {
  subscription: NonNullable<Get_SubscriptionQuery["subscription"]>;
}) {
  const { t } = useTranslation("common");

  return (
    <>
      {subscription.status === SubscriptionStatus.InTrial && (
        <SubscriptionDetailRow className="mt-6">
          <SubscriptionDetailTitleRow
            first={
              <span>
                {t("days_free_trial", {
                  days: subscription.purchaseOption.trialDays,
                })}
              </span>
            }
            second={
              <span>
                {t("free")} <span className="text-white/70">(0,00 kr)</span>
              </span>
            }
          />

          <SubscriptionDetailSubtitleRow className="text-comp-yellow-600">
            {t("ending")} {strftime("%B %d, %Y", new Date(subscription.trialPeriodEndsAt))}
          </SubscriptionDetailSubtitleRow>
        </SubscriptionDetailRow>
      )}
      <SubscriptionDetailRow>
        <SubscriptionDetailTitleRow
          first={
            subscription.purchaseOption.billingFrequency === BillingFrequency.Monthly ? (
              <span>
                {t("Monthly")} {t("subscription")}
              </span>
            ) : (
              <span>
                {t("Yearly")} {t("subscription")}
              </span>
            )
          }
          second={Intl.NumberFormat("sv-SE", {
            style: "currency",
            currency: subscription.purchaseOption.price.currency || "kr",
          }).format(Number(subscription.purchaseOption.price.price))}
        />

        <SubscriptionDetailSubtitleRow className="text-white/50">
          {subscription.status === SubscriptionStatus.InTrial && (
            <>
              {t("starting")} {strftime("%B %d, %Y", new Date(subscription.nextBillingAt))}
            </>
          )}

          {subscription.status === SubscriptionStatus.Active && (
            <>
              {t("next_billing")} {strftime("%B %d, %Y", new Date(subscription.nextBillingAt))}
            </>
          )}
        </SubscriptionDetailSubtitleRow>
      </SubscriptionDetailRow>
      {subscription.premiumUserEmail && (
        <SubscriptionDetailRow>
          <CopyToClipboardButton
            text={subscription.premiumUserEmail}
            className="group w-full rounded text-left focus:shadow-input-focus focus:outline-none focus:ring-0"
          >
            <SubscriptionDetailTitleRow
              first={subscription.premiumUserEmail}
              second={<CopyIcon srText={t("copy_email_address")} />}
            />
            <SubscriptionDetailSubtitleRow className="text-white/50">
              {t("account")}
            </SubscriptionDetailSubtitleRow>
          </CopyToClipboardButton>
        </SubscriptionDetailRow>
      )}
      {subscription.discount && (
        <SubscriptionDetailRow>
          <SubscriptionDetailTitleRow
            first={<span>{t("discount")}</span>}
            second={subscription.discount.discountFormatted}
          />
          <SubscriptionDetailSubtitleRow className="text-comp-yellow-600">
            {t("code")}: {subscription.discount.name}
            {subscription.discount.endTime &&
              `, ${t("ending")} ${strftime("%B %d, %Y", new Date(subscription.discount.endTime))}`}
          </SubscriptionDetailSubtitleRow>
        </SubscriptionDetailRow>
      )}
      {subscription.reference && (
        <SubscriptionDetailRow>
          <CopyToClipboardButton
            text={subscription.reference || ""}
            className="group w-full rounded text-left focus:shadow-input-focus focus:outline-none focus:ring-0"
          >
            <SubscriptionDetailTitleRow
              first={subscription.reference}
              second={<CopyIcon srText={t("copy_reference_code")} />}
            />
            <SubscriptionDetailSubtitleRow className="text-white/50">
              {t("reference_code")}
            </SubscriptionDetailSubtitleRow>
          </CopyToClipboardButton>
        </SubscriptionDetailRow>
      )}
      {subscription.cancelAt ? (
        <CancellationMessage subscription={subscription} />
      ) : (
        <>
          {subscription.nextBillingAt && (
            <p className="mt-6 text-xs text-white/70">
              {t("renewal_message", {
                date: strftime("%B %d, %Y", new Date(subscription.nextBillingAt)),
              })}
            </p>
          )}
          <p className="mt-2 text-xs text-white/70">
            <ButtonOrLink
              href={subscription.manageUrl}
              className="rounded text-white underline transition hover:text-white/70 focus:shadow-input-focus focus:outline-none focus:ring-0"
            >
              {t("cancel_subscription")}
            </ButtonOrLink>{" "}
            {t("cancel_hint")}
          </p>
          <CustomerSupportMessage />
        </>
      )}
    </>
  );
}
